import { t } from '@/utils/i18n'
import { useRef, useEffect } from 'react'
import { SwipeAction, Checkbox, Dialog, Toast } from 'antd-mobile'
import { useNavigate } from 'react-router-dom'
import { useSetState } from 'ahooks'
import { utils, image, productUtil, common, constant } from '@/utils'
import type {
  ProductItem as IProductItem,
  ProductDetail,
} from '@/types/product'
import { useCorpSettingStore } from '@/stores'
import Price from '@/components/Price'
import Thumb from '@/components/Thumb'
import Stepper from '@/components/Stepper'
import ShippingTypeLabel from '@/components/ShippingTypeLabel'
import { Action } from 'antd-mobile/es/components/swipe-action'
import styles from './index.module.less'
import ConvertOrderQuantity from '@/components/ConvertOrderQuantity/ConvertOrderQuantity'

const { to, concatenateImageUrl } = utils
const { imgFormatter } = image
const { getChooseUnit } = common
const {
  product: { IMAGE_DEFAULT },
} = constant
interface IProps {
  className: string
  productData: IProductItem
  onCheck: (productData: IProductItem) => void
  onDeleteProd: () => void
  onUpdateProdQuantity: (data: { quantity: number, detailId: string }) => void
}

const ProductItem = (props: IProps) => {
  const { productData } = props
  const saleProduct = productData.saleProduct || {}
  const cloudProduct = productData.cloudProduct || {}
  const { productStatus } = cloudProduct
  const isMultiple = (productStatus & 4) === 4 // 是否是多规格数据
  const remark = saleProduct.remark || ''
  const details: ProductDetail[] = saleProduct.details || []
  const isHaveSkuImg = details.findIndex((item) => item.image) > -1

  // 单规格数据
  const specData = details[0] || {}
  const { corpSetting } = useCorpSettingStore()
  const { currencySymbol } = corpSetting || {}
  const navigate = useNavigate()

  const swipeRef = useRef<any>(null)
  const [thumbInfo, setThumbInfo] = useSetState({
    url: '',
  })
  let lastImgUrl = ''
  const swipeActions: Action[] = [
    {
      key: 'delete',
      text: t('删除'),
      color: 'danger',
    },
  ]

  const chooseUnit = getChooseUnit(productData, corpSetting, specData)

  useEffect(() => {
    dataInit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudProduct])

  // 待处理数据
  function dataInit() {
    initThumb()
  }

  // 初始化缩略图
  async function initThumb() {
    // 有图时拿首图
    const { imageList, video } = cloudProduct || {}
    const imgUrl = (imageList && imageList[0]) || video || ''

    if (imgUrl === lastImgUrl) {
      return
    }

    const [err, res] = await to(imgFormatter(imgUrl, 260))

    lastImgUrl = imgUrl
    setThumbInfo({
      url: err ? imgUrl : res,
    })
  }

  // 点击商品进入详情页
  function gotoDetail() {
    if (saleProduct.conflictStatus === 1 || saleProduct.conflictStatus === 2) {
      Toast.show(t('当前商品下架已删除'))
      return
    }

    navigate(`/product/detail/${saleProduct?.productId}`)
  }

  // 勾选货品
  function checkProduct(productData: IProductItem) {
    const selected = !productData.saleProduct.selected
    productData.saleProduct.selected = Number(selected)
    props.onCheck(productData)
  }

  // 更新货品,将收到的参数往上传递
  function updateProdQuantity(quantity: number, detailId: string) {
    props.onUpdateProdQuantity({ quantity, detailId })
  }

  // 左滑操作
  function onSwipe(action: Action) {
    // 删除货品
    if (action.key === 'delete') {
      Dialog.confirm({
        title: t('确认删除货品吗？'),
        confirmText: t('确定'),
        cancelText: t('取消'),
      }).then((isConfirm) => {
        if (isConfirm) {
          props.onDeleteProd()
          swipeRef.current.close()
        }
      })
    }
  }

  return (
    <SwipeAction ref={swipeRef} rightActions={swipeActions} onAction={onSwipe}>
      <div className={styles['product-wrap']}>
        <div className={styles['checkbox-box']}>
          <Checkbox checked={saleProduct?.selected}
            onClick={() => checkProduct(productData)}></Checkbox>
        </div>

        <div className={styles['product-container']}>
          <div className={styles['product-top']}>
            <div
              className={styles['product-left']}
              onClick={() => checkProduct(productData)}
            >
              {/* 商品图片 */}
              <Thumb
                extClass="product-thumb"
                width={164}
                height={164}
                thumb={thumbInfo}
                has-video={!!cloudProduct?.video}
              />
            </div>

            <div className={ `${styles['product-content']}` } onClick={gotoDetail}>
              {/* 商品标题行 */}
              <div className={`${styles['product-title']} ep-2`}>
                {cloudProduct?.highlight && (
                  <div dangerouslySetInnerHTML={cloudProduct?.highlight}></div>
                )}

                {!cloudProduct?.highlight && (
                  <>
                    {/* 商品标签 */}
                    <span className={productUtil.getRecommendClass(cloudProduct)}>
                      {productUtil.getRecommendTag(cloudProduct)}
                    </span>
                    {/* 商品标题 */}
                    <span>{cloudProduct?.productName || ''}</span>
                  </>
                )}
              </div>

              <ShippingTypeLabel shippingType={cloudProduct?.deliveryType} />

              {/* 单规格货品 */}
              {
                !isMultiple && (
                  <>
                    <Price
                      price={chooseUnit.priceText}
                      originPrice={chooseUnit.originPriceText}
                      discount={chooseUnit.discount}
                      priceUnitName={specData.priceUnitName}
                    />

                    <div className={styles['stepper-box']}>
                      <Stepper
                        initialValue={specData?.quantity || 0}
                        stock={chooseUnit.stock || 0}
                        quantity={chooseUnit.productMoqQty || 0}
                        unitName={chooseUnit.unitName || ''}
                        onChange={ quantity => updateProdQuantity(Number(quantity), specData.saleDetailId) }
                      />
                      <ConvertOrderQuantity
                        moqUnitIndex={cloudProduct?.productMoqUnitIndex}
                        quantity={specData?.quantity || 0}
                        unitList={cloudProduct?.unitPriceArray || []}
                      />
                    </div>
                  </>
                )
              }
            </div>
          </div>

          {
            remark && (
              <div className={ `${styles["product-remark"]} ep-2` }>{ t('备注')}：{ remark }</div>
            )
          }

          {/* 多规格货品 */}
          {
            isMultiple && (
              <div className={styles['multi-sku-section']}>
                {
                  !!details.length && details.map((detail) => {
                    const detailUnit = getChooseUnit(productData, corpSetting, detail)
                    const { stock, productMoqQty, unitName, discount, discountText, price, originPrice, priceText, originPriceText } = detailUnit
                    const { elements, priceUnitName } = detail
                    const elementNames = elements.map(ele => ele.elementName).join('，')
                    return (
                      <div className={ styles['detail-box'] } key={ detail.skuId }>
                        <div className={ styles['element-names'] }>
                          { isHaveSkuImg && (
                            <div className={styles['img-wrap']}>
                              <img src={detail.image ? concatenateImageUrl(detail.image + '_320') : IMAGE_DEFAULT} />
                            </div>
                          )}
                          <p className={ styles['title'] }>{ elementNames }</p>
                        </div>
                        <div className={ styles['sku-info'] }>
                          <div className={ styles['price-info'] }>
                            <div className={ styles['price'] }>
                              { currencySymbol } { priceText }
                              {
                                priceUnitName &&
                                <span>/{ priceUnitName }</span>
                              }
                            </div>
                            {
                              !!originPriceText && originPrice > price &&
                              <div className={ styles['origin-price'] }>{ currencySymbol }{ originPriceText }</div>
                            }
                            {
                              !!discount &&
                              <span className={ styles["discount-box"] }>
                                -{ discountText }
                              </span>
                            }
                          </div>
                          <div className={styles['stepper-box']}>
                            <Stepper
                              initialValue={detail?.quantity}
                              stock={stock || 0}
                              quantity={productMoqQty || 0}
                              unitName={unitName || ''}
                              onChange={quantity => updateProdQuantity(Number(quantity), detail.saleDetailId)}
                            />
                            <ConvertOrderQuantity
                              moqUnitIndex={cloudProduct?.productMoqUnitIndex}
                              quantity={detail.quantity}
                              unitList={cloudProduct?.unitPriceArray || []}
                            />
                          </div>
                        </div>
                        { detail.skuRemark && (
                            <div className={styles['sku-remark']}>
                              <span className={`${styles["product-remark"]} ep-2`}>{ t('备注')}：{detail.skuRemark}</span>
                            </div>
                          )
                        }
                      </div>
                    )
                  })
                }
              </div>
            )
          }
        </div>
      </div>
    </SwipeAction>
  )
}

export default ProductItem
